import axios from 'axios';
import { SERVICE_CATALOG } from '../mock';

const ENV_API_BASE = process.env.REACT_APP_API_BASE_URL
  || process.env.REACT_APP_API_URL
  || process.env.VITE_API_BASE_URL
  || process.env.VITE_API_URL
  || '';
const RUNTIME_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const API_BASE = ENV_API_BASE || RUNTIME_ORIGIN;

const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';

const MONTHLY_SERVICE_CODES = new Set([
  'MONTHLY_ACCOUNTING',
  'PAYROLL_PROCESSING',
  'PF_FILING',
  'ESI_FILING',
]);

/** @typedef {{ entity_type: string, value: string, filter?: string|null }} PaymentLookup */

/**
 * Public API: requires entity_type + value; optional filter query param.
 * @param {PaymentLookup} spec
 * @returns {Promise<object|null>} Single payment_config row or null.
 */
export async function fetchPublicPaymentPrice(spec) {
  if (!API_BASE || !spec?.entity_type || !spec?.value) return null;
  const params = {
    entity_type: spec.entity_type,
    value: spec.value,
  };
  if (spec.filter != null && String(spec.filter).trim() !== '') {
    params.filter = spec.filter;
  }
  const requestOnce = async () => {
    const res = await axios.get(
      `${API_BASE}/api/v1/payments_config/payment-config/public`,
      {
        params,
        headers: {
          'Content-Type': 'application/json',
          ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
        },
        timeout: 10000,
      },
    );
    return res.data?.data;
  };
  const pickFromPayload = (payload) => {
    // New API shape: data is a single object row.
    if (payload && !Array.isArray(payload) && typeof payload === 'object') return payload;
    // Backward-compatible shape: data is an array for an entity_type.
    if (Array.isArray(payload)) {
      const wantedValue = String(spec.value).trim().toUpperCase();
      const wantedFilter = spec.filter != null ? String(spec.filter).trim().toUpperCase() : null;
      const normalized = payload.filter((r) => String(r?.config_type || '').toUpperCase() === 'PRICE');
      if (!normalized.length) return null;
      const byValue = normalized.filter((r) => String(r?.value || '').trim().toUpperCase() === wantedValue);
      if (!byValue.length) return null;
      // If caller passed filter, prefer exact filter, then null filter.
      if (wantedFilter) {
        const exact = byValue.find((r) => String(r?.filter || '').trim().toUpperCase() === wantedFilter);
        if (exact) return exact;
      }
      const noFilter = byValue.find((r) => r?.filter == null || String(r?.filter).trim() === '');
      return noFilter || byValue[0] || null;
    }
    return null;
  };
  try {
    const payload = await requestOnce();
    return pickFromPayload(payload);
  } catch (err) {
    // Retry once for any transient issue (network, timeout, 429/5xx).
    try {
      await new Promise((r) => setTimeout(r, 180));
      const payload = await requestOnce();
      return pickFromPayload(payload);
    } catch {
      // no-op
    }
    // Keep UI resilient, but log so pricing issues are visible in browser console.
    // eslint-disable-next-line no-console
    console.warn('pricing fetch failed', { spec, base: API_BASE, err });
    return null;
  }
}

const formatAmount = (amount, monthly = false) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return null;
  const base = `INR ${num.toLocaleString('en-IN')} `;
  return monthly ? `${base}/ month onwards` : `${base}onwards`;
};

/**
 * @param {PaymentLookup[]} queries
 * @param {'min'|'first'} mode
 */
async function resolveAmounts(queries, mode = 'first') {
  if (mode === 'min') {
    const amounts = [];
    await Promise.all(
      queries.map(async (q) => {
        const row = await fetchPublicPaymentPrice(q);
        if (row && row.amount != null) amounts.push(Number(row.amount));
      }),
    );
    if (!amounts.length) return null;
    return Math.min(...amounts);
  }
  for (let i = 0; i < queries.length; i += 1) {
    const row = await fetchPublicPaymentPrice(queries[i]);
    if (row && row.amount != null) return Number(row.amount);
  }
  return null;
}

/** One or more API lookups per catalog service code (matches your payment_config rows). */
const SERVICE_PRICE_SPECS = [
  {
    code: 'ITR_FILING',
    mode: 'first',
    queries: [
      { entity_type: 'INCOME_TAX', value: 'DEFAULT' },
      { entity_type: 'INCOME_TAX', value: 'SALARY' },
      { entity_type: 'INCOME_TAX', value: 'BUSINESS' },
      { entity_type: 'INCOME_TAX', value: 'PROFESSION' },
      { entity_type: 'INCOME_TAX', value: 'HOUSE_PROPERTY' },
      { entity_type: 'INCOME_TAX', value: 'CAPITAL_GAINS' },
      { entity_type: 'INCOME_TAX', value: 'OTHER_SOURCES' },
      { entity_type: 'INCOME_TAX', value: 'MULTIPLE_SOURCES' },
    ],
  },
  { code: 'ITR_NOTICE', queries: [{ entity_type: 'INCOME_TAX', value: 'ITR_NOTICE' }] },
  { code: 'ADVANCE_TAX', queries: [{ entity_type: 'INCOME_TAX', value: 'ADVANCE_TAX' }] },
  {
    code: 'CAPITAL_GAINS',
    queries: [
      { entity_type: 'INCOME_TAX', value: 'CAPITAL_GAINS_CALC' },
      { entity_type: 'INCOME_TAX', value: 'CAPITAL_GAINS' },
    ],
  },

  {
    code: 'GST_REGISTRATION',
    mode: 'first',
    queries: [
      { entity_type: 'GST_REGISTRATION', value: 'DEFAULT' },
      { entity_type: 'GST_REGISTRATION', value: 'PROPRIETARY' },
      { entity_type: 'GST_REGISTRATION', value: 'PARTNERSHIP_FIRM' },
      { entity_type: 'GST_REGISTRATION', value: 'COMPANY' },
    ],
  },
  { code: 'GST_AMENDMENT', queries: [{ entity_type: 'GST', value: 'GST_AMENDMENT' }] },
  { code: 'GST_CANCELLATION', queries: [{ entity_type: 'GST', value: 'GST_CANCELLATION' }] },
  { code: 'GST_FILING', queries: [{ entity_type: 'GST_FILING', value: 'GST_FILING' }] },
  { code: 'GST_Q_FILING', queries: [{ entity_type: 'GST_FILING', value: 'GST_Q_FILING' }] },
  { code: 'GST_ANNUAL_RETURN', queries: [{ entity_type: 'GST_FILING', value: 'GST_ANNUAL_RETURN' }] },
  { code: 'GST_LUT', queries: [{ entity_type: 'GST', value: 'GST_LUT' }] },
  { code: 'GST_REFUND', queries: [{ entity_type: 'GST', value: 'GST_REFUND' }] },
  { code: 'GST_NOTICE_REPLY', queries: [{ entity_type: 'GST', value: 'GST_NOTICE_REPLY' }] },

  { code: 'PVT_LTD_REG', queries: [{ entity_type: 'COMPANY', value: 'PVT_LTD_REG' }] },
  { code: 'LLP_REG', queries: [{ entity_type: 'COMPANY', value: 'LLP_REG' }] },
  { code: 'OPC_REG', queries: [{ entity_type: 'COMPANY', value: 'OPC_REG' }] },
  { code: 'PARTNERSHIP_REG', queries: [{ entity_type: 'COMPANY', value: 'PARTNERSHIP_REG' }] },

  { code: 'ROC_ANNUAL', queries: [{ entity_type: 'MCA', value: 'ROC_ANNUAL' }] },
  { code: 'DIR3_KYC', queries: [{ entity_type: 'MCA', value: 'DIR3_KYC' }] },
  { code: 'DIRECTOR_CHANGE', queries: [{ entity_type: 'MCA', value: 'DIRECTOR_CHANGE' }] },

  { code: 'MONTHLY_ACCOUNTING', queries: [{ entity_type: 'ACCOUNTING', value: 'MONTHLY_ACCOUNTING' }] },
  { code: 'YEAR_END_FINALIZATION', queries: [{ entity_type: 'ACCOUNTING', value: 'YEAR_END_FINALIZATION' }] },

  { code: 'PAYROLL_PROCESSING', queries: [{ entity_type: 'PAYROLL', value: 'PAYROLL_PROCESSING' }] },
  { code: 'PF_FILING', queries: [{ entity_type: 'PAYROLL', value: 'PF_FILING' }] },
  { code: 'ESI_FILING', queries: [{ entity_type: 'PAYROLL', value: 'ESI_FILING' }] },

  { code: 'TRADEMARK_REG', queries: [{ entity_type: 'TRADEMARK', value: 'TRADEMARK_REG' }] },
  { code: 'TRADEMARK_RENEWAL', queries: [{ entity_type: 'TRADEMARK', value: 'TRADEMARK_RENEWAL' }] },

  { code: 'MSME_REG', queries: [{ entity_type: 'LICENSE', value: 'MSME_REG' }] },
  { code: 'FSSAI_LICENSE', queries: [{ entity_type: 'LICENSE', value: 'FSSAI_LICENSE' }] },
  { code: 'IEC_REG', queries: [{ entity_type: 'LICENSE', value: 'IEC_REG' }] },
];

const PRICE_SPEC_BY_CODE = new Map(SERVICE_PRICE_SPECS.map((item) => [item.code, item]));
const SERVICE_AMOUNT_CACHE = new Map();
const SERVICE_AMOUNT_CACHE_TTL_MS = 5 * 60 * 1000;
const PREFETCH_CONCURRENCY = 6;

function setCachedAmount(serviceCode, amount) {
  SERVICE_AMOUNT_CACHE.set(serviceCode, { amount, ts: Date.now() });
}

export function getCachedServicePriceAmount(serviceCode) {
  const cached = SERVICE_AMOUNT_CACHE.get(serviceCode);
  if (!cached) return null;
  if ((Date.now() - cached.ts) > SERVICE_AMOUNT_CACHE_TTL_MS) {
    SERVICE_AMOUNT_CACHE.delete(serviceCode);
    return null;
  }
  return cached.amount;
}

const catalogCodes = new Set(SERVICE_CATALOG.map((s) => s.code));
SERVICE_PRICE_SPECS.forEach((spec) => {
  if (!catalogCodes.has(spec.code)) {
    console.warn('pricingConfigClient: unknown service code', spec.code);
  }
});

export async function fetchServicePricingFromBackend() {
  if (!API_BASE) {
    // eslint-disable-next-line no-console
    console.warn('pricing fetch skipped: API base URL is empty');
    return {};
  }

  // Backend-first: one cached API call returns service-code -> amount map.
  try {
    const res = await axios.get(
      `${API_BASE}/api/v1/payments_config/payment-config/public/service-prices`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
        },
        timeout: 10000,
      },
    );
    const amountMap = res?.data?.data;
    if (amountMap && typeof amountMap === 'object') {
      const nextPriceMap = {};
      Object.entries(amountMap).forEach(([serviceCode, amount]) => {
        const n = Number(amount);
        if (!Number.isFinite(n)) return;
        setCachedAmount(serviceCode, n);
        const monthly = MONTHLY_SERVICE_CODES.has(serviceCode);
        const label = formatAmount(n, monthly);
        if (label) nextPriceMap[serviceCode] = label;
      });
      if (Object.keys(nextPriceMap).length > 0) return nextPriceMap;
    }
  } catch (err) {
    // fallback to legacy per-service calls below
    // eslint-disable-next-line no-console
    console.warn('bulk pricing endpoint failed; falling back to per-service', err);
  }

  const nextPriceMap = {};
  const runPool = async (items, worker, concurrency = PREFETCH_CONCURRENCY) => {
    const queue = [...items];
    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        // eslint-disable-next-line no-await-in-loop
        await worker(item);
      }
    });
    await Promise.all(workers);
  };

  await runPool(SERVICE_PRICE_SPECS, async (spec) => {
    const mode = spec.mode || 'first';
    const amt = await resolveAmounts(spec.queries, mode);
    if (amt == null) return;
    setCachedAmount(spec.code, amt);
    const monthly = MONTHLY_SERVICE_CODES.has(spec.code);
    const label = formatAmount(amt, monthly);
    if (label) nextPriceMap[spec.code] = label;
  });

  // Retry pass only for still-missing codes.
  const missingSpecs = SERVICE_PRICE_SPECS.filter((spec) => !nextPriceMap[spec.code]);
  await runPool(missingSpecs, async (spec) => {
    const mode = spec.mode || 'first';
    const amt = await resolveAmounts(spec.queries, mode);
    if (amt == null) return;
    setCachedAmount(spec.code, amt);
    const monthly = MONTHLY_SERVICE_CODES.has(spec.code);
    const label = formatAmount(amt, monthly);
    if (label) nextPriceMap[spec.code] = label;
  }, 4);
  return nextPriceMap;
}

export async function fetchServicePriceAmount(serviceCode) {
  const cached = getCachedServicePriceAmount(serviceCode);
  if (cached != null) return cached;
  const spec = PRICE_SPEC_BY_CODE.get(serviceCode);
  if (!spec) return null;
  const mode = spec.mode || 'first';
  const amt = await resolveAmounts(spec.queries, mode);
  if (amt != null) setCachedAmount(serviceCode, amt);
  return amt;
}

export async function prefetchAllServicePriceAmounts() {
  await Promise.all(SERVICE_PRICE_SPECS.map(async (spec) => {
    const amt = await fetchServicePriceAmount(spec.code);
    if (amt != null) setCachedAmount(spec.code, amt);
  }));
}

/**
 * ITR source table: fill `price` from API using per-row payment lookup.
 * @param {Array<{ source: string, itr: string, price: string, docs: string, payment?: PaymentLookup }>} baseGuide
 */
export async function hydrateItrPricingGuideRows(baseGuide) {
  if (!API_BASE || !baseGuide?.length) return baseGuide;
  const out = await Promise.all(
    baseGuide.map(async (row) => {
      if (Array.isArray(row.paymentVariants) && row.paymentVariants.length > 0) {
        const variantRows = await Promise.all(
          row.paymentVariants.map(async (variant) => {
            const data = await fetchPublicPaymentPrice(variant);
            const amt = data?.amount;
            if (amt == null || !Number.isFinite(Number(amt))) return null;
            return {
              amount: Number(amt),
              label: variant.label || '',
            };
          }),
        );
        const present = variantRows.filter(Boolean);
        if (present.length > 0) {
          const price = present
            .map((v) => `${v.amount.toLocaleString('en-IN')} for ${v.label}`)
            .join(' / ');
          return { ...row, price };
        }
      }
      if (!row.payment) return row;
      const data = await fetchPublicPaymentPrice(row.payment);
      const amt = data?.amount;
      if (amt == null || !Number.isFinite(Number(amt))) return row;
      const price = `INR ${Number(amt).toLocaleString('en-IN')}`;
      return { ...row, price };
    }),
  );
  return out;
}
